import type { ReviewPeriod } from './types';

/** Sentry feature tag for errors surfaced inside this module. */
export const REVIEWS_FEATURE_TAG = 'reviews';

/**
 * Query keys of the review report data Review Tracker and Review Manager read. A finished run changes all of it,
 * so a run that ends on either page marks the other page's data stale too.
 */
export const REVIEW_REPORT_DATA_QUERY_KEYS = {
  /** The report record: its schedule and `updatedAt`, which both page headers show as "Last updated". */
  REPORT: 'reviewReport',
  /** Review Tracker's review totals. */
  TRACKER_PERFORMANCE: 'reviewTrackerPerformance',
  /** Review Tracker's period × source × rating grid. */
  TRACKER_DIRECTORY_BREAKDOWN: 'reviewTrackerDirectoryBreakdown',
  /** Review Manager's reviews list. */
  MANAGER_REVIEWS: 'reviewManagerReviews',
  /** Review Manager's All count. */
  MANAGER_ALL_REVIEWS_COUNT: 'reviewManagerAllReviewsCount'
} as const;

/**
 * Query key of the review sites Reputation Manager knows for a location's country. Kept out of
 * {@link REVIEW_REPORT_DATA_QUERY_KEYS}: a run does not change the catalogue.
 */
export const REVIEW_DIRECTORIES_QUERY_KEY = 'reviewDirectories';

/** Time periods the review pages offer. */
export const REVIEW_PERIOD = {
  LAST_30_DAYS: 'last_30_days',
  LAST_3_MONTHS: 'last_3_months',
  LAST_6_MONTHS: 'last_6_months',
  LAST_12_MONTHS: 'last_12_months',
  ALL_TIME: 'all_time'
} as const;

/** i18n label key of each time period, shown in a Time period select. */
export const PERIOD_LABEL_KEYS = {
  [REVIEW_PERIOD.LAST_30_DAYS]: 'reviewFilters.periods.last30Days',
  [REVIEW_PERIOD.LAST_3_MONTHS]: 'reviewFilters.periods.last3Months',
  [REVIEW_PERIOD.LAST_6_MONTHS]: 'reviewFilters.periods.last6Months',
  [REVIEW_PERIOD.LAST_12_MONTHS]: 'reviewFilters.periods.last12Months',
  [REVIEW_PERIOD.ALL_TIME]: 'reviewFilters.periods.allTime'
} as const satisfies Record<ReviewPeriod, string>;

/** Sizes of a filter trigger: the desktop toolbar, and the small phone button. */
export const FILTER_TRIGGER_SIZE = {
  DEFAULT: 'default',
  SM: 'sm'
} as const;

/** Error codes of the Reputation Manager endpoints the review pages handle themselves. */
export const RM_ERROR_CODES = {
  /** Directories: the location's country is not supported. */
  COUNTRY_NOT_SUPPORTED: 'COUNTRY_NOT_SUPPORTED',
  /** Run: the subscription has no manual runs left. */
  REPORT_RUN_QUOTA_EXCEEDED: 'REPORT_RUN_QUOTA_EXCEEDED',
  /** Run: a run is already in progress. */
  REPORT_ALREADY_RUNNING: 'REPORT_ALREADY_RUNNING',
  /** Report: the report does not exist, was deleted or is not linked to a location. */
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  /** Report: the report belongs to another account. */
  REPORT_ACCESS_DENIED: 'REPORT_ACCESS_DENIED',
  /** Update Review: auto-replies or the review-removal flow own the review's status, so it can't be changed. */
  REVIEW_STATUS_LOCKED: 'REVIEW_STATUS_LOCKED'
} as const;

/** Screens a page that reads the review report can show; the page shows exactly one. */
export const REVIEW_REPORT_SCREEN = {
  /** The location has no review report. */
  NOT_SET_UP: 'not_set_up',
  /** The report request hasn't answered yet, so the page can't tell whether the report is there. */
  LOADING: 'loading',
  /** The location's report can't be read: it is gone or belongs to another account. */
  REPORT_UNAVAILABLE: 'report_unavailable',
  /** The report is running for the first time, so there is no data yet. */
  FIRST_RUN: 'first_run',
  /** The report has run but found no reviews for all time. */
  NO_REVIEWS: 'no_reviews',
  /** The report itself: the page's own cards or table. */
  REPORT: 'report'
} as const;

/** Why a requested run did not start, as the page tells it. */
export const RUN_REPORT_REFUSAL = {
  /** `422 REPORT_RUN_QUOTA_EXCEEDED`: an expected outcome, not reported. */
  QUOTA_EXCEEDED: 'quota_exceeded',
  /** Any other failure of the run request. */
  FAILED: 'failed'
} as const;
